import React from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import ReactionBar from '../../components/social/ReactionBar';
import { getMyBookmarks, tripReaction } from '../../../services/engagement/api';
import { Trip, getTripById } from '../../../services/trips/api/tripsApi';
import { extractTripUuid } from '../../../services/trips/utils/idUtils';
import './BookmarksPage.css';

const formatDates = (trip: Trip): string => {
  const start = trip.dates?.startDate;
  const end = trip.dates?.endDate;
  if (!start) return 'Dates TBD';
  const fmt = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return end && end !== start ? `${fmt(start)} – ${fmt(end)}` : fmt(start);
};

const BookmarksPage: React.FC = () => {
  const [trips, setTrips] = React.useState<Trip[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { tripIds } = await getMyBookmarks();
        if (tripIds.length === 0) {
          if (!cancelled) setTrips([]);
          return;
        }
        // Trip reads arrive enriched with likes + viewer state (T2.14), so the
        // cards need no separate reactions call.
        const settled = await Promise.allSettled(tripIds.map((id) => getTripById(id)));
        if (cancelled) return;
        setTrips(settled.filter((r): r is PromiseFulfilledResult<Trip> => r.status === 'fulfilled').map((r) => r.value));
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load your bookmarks.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <DashboardLayout activeSection="trips">
      <div className="v2-bookmarks-page">
        <header className="v2-bookmarks-page__header">
          <h1>Saved trips</h1>
          <p>Trips you bookmarked to plan or revisit later.</p>
        </header>

        {loading && <p className="v2-bookmarks-page__state">Loading your bookmarks…</p>}
        {!loading && error && <p className="v2-bookmarks-page__state v2-bookmarks-page__state--error">{error}</p>}
        {!loading && !error && trips.length === 0 && (
          <p className="v2-bookmarks-page__state">No bookmarks yet. Tap the bookmark icon on any trip to save it here.</p>
        )}

        {!loading && !error && trips.length > 0 && (
          <div className="v2-bookmarks-grid">
            {trips.map((trip) => {
              const uuid = extractTripUuid(trip.id);
              return (
                <article key={String(trip.id)} className="v2-bookmark-card">
                  <Link to={`/trips/${uuid}`} state={{ backTo: '/bookmarks' }} className="v2-bookmark-card__body">
                    <h3>{trip.title}</h3>
                    <p className="v2-bookmark-card__dest">{(trip.destinations || []).join(' · ') || '—'}</p>
                    <p className="v2-bookmark-card__dates">{formatDates(trip)}</p>
                  </Link>
                  <ReactionBar tripId={uuid} reaction={tripReaction(trip)} />
                </article>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BookmarksPage;
